"use client";

// Third-party Imports
import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { userTeamsTypes } from "@/types/apps/userTeamsTypes";

// Server Imports
import { doGetuserTeams } from "@/app/server/actions";

const initialState: userTeamsTypes = {
  teams: [],
  filteredTeams: [],
  selectedTeamMembers: [],
  filteredSelectedTeamMembers: [],
  loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  loadingSelected: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  error: null,
  total: 0,
  selectedTypes: ["Member", "Owner"],
  lastErrorToastTime: 0,
} as any;

const filterEventsUsingCheckbox = (teams: any[], selectedTypes: any[]) => {
  if (selectedTypes.length === 0) return [];
  if (!selectedTypes.includes("Member") && !selectedTypes.includes("Owner")) return teams;
  if (selectedTypes.includes("Member") && selectedTypes.includes("Owner")) return teams;
  if (!selectedTypes.includes("Member") && selectedTypes.includes("Owner")) return teams.filter(team => team.owner);
  if (selectedTypes.includes("Member") && !selectedTypes.includes("Owner")) return teams.filter(team => team.member);
};

export const fetch = createAsyncThunk<any, any, any>(
  "user_teams/fetch",
  async (_: void, { rejectWithValue }) => {
    const response = await doGetuserTeams();

    // If response is null, it means a 500 error occurred
    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load teams");
    }

    // Ensure response is an array
    const teams = Array.isArray(response) ? response : [];

    return {
      data: teams,
      total: teams.length,
    };
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as any;

      if (state.userTeamsReducer.loading === "pending") {
        return false;
      }
    },
  }
);

export const fetchMembers = createAsyncThunk<any, any, any>("user_teams/fetch_members", async (members: any, { rejectWithValue }) => {
  try {
    if (members.length > 0) {
      for (let i = 0; i < members.length; i++) {
        if (typeof members[i].id === "undefined") members[i].id = i + 1; // Add 1 to avoid id of 0
      }

      return {
        data: members,
        total: members.length,
      };
    }

    return {
      data: [],
      total: 0,
    };
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const userTeamsSlice = createSlice({
  name: "userTeams",
  initialState: initialState,
  reducers: {
    filterLabel: (state: any, action) => {
      const { key, search } = action.payload;
      const index = state.selectedTypes.indexOf(key);

      if (index !== -1) {
        state.selectedTypes.splice(index, 1);
      } else {
        state.selectedTypes.push(key);
      }

      const currentState = current(state);
      let data: any = currentState.filteredTeams;

      data = filterEventsUsingCheckbox(data, currentState.selectedTypes);

      if (search && search.length > 0) {
        data = data.filter((team: any) => {
          return team.name.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.teams = data;
    },
    filterAllLabels: (state: any, action) => {
      const { checked, search } = action.payload;

      state.selectedTypes = checked ? ["Member", "Owner"] : [];
      const currentState = current(state);

      let data: any = currentState.filteredTeams;

      data = filterEventsUsingCheckbox(data, currentState.selectedTypes);

      if (search && search.length > 0) {
        data = data.filter((federation: any) => {
          return federation.name.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.teams = data;
    },
    filterBytext: (state: any, action) => {
      const { search } = action.payload;
      const currentState = current(state);
      let data: any = currentState.filteredTeams;

      if (search && search.length > 0) {
        data = data.filter((team: any) => {
          return team.name.toLowerCase().includes(search.toLowerCase());
        });
      }

      data = filterEventsUsingCheckbox(data, currentState.selectedTypes);

      state.teams = data;
    },
    filterMembersBytext: (state: any, action) => {
      const currentState = current(state);

      if (currentState.filteredSelectedTeamMembers.length > 0) {
        state.selectedTeamMembers = currentState.filteredSelectedTeamMembers.filter((team: any) => {
          const fullname = JSON.parse(team.details[0].details);

          return fullname.fullname.toLowerCase().includes(action.payload.toLowerCase());
        });
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetch.pending, state => {
      state.loading = "idle";
    });
    builder.addCase(fetch.rejected, (state, action) => {
      // Don't show error if the request was aborted
      if (action.meta.aborted) {
        state.loading = "idle";
        return;
      }

      state.loading = "failed";
      state.error = action.payload;

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load teams. Please try again.", {
          toastId: "teams-load-error",
        });
      }
    });
    builder.addCase(fetch.fulfilled, (state, action) => {
      state.teams = action.payload.data;
      state.filteredTeams = action.payload.data;
      state.total = action.payload.total;
      state.loading = "succeeded";
    });

    builder.addCase(fetchMembers.pending, state => {
      state.loadingSelected = "idle";
      state.selectedTeamMembers = [];
    });
    builder.addCase(fetchMembers.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.loadingSelected = "idle";
        return;
      }

      state.loadingSelected = "failed";
      state.error = action.payload;
    });
    builder.addCase(fetchMembers.fulfilled, (state, action) => {
      state.selectedTeamMembers = action.payload.data;
      state.filteredSelectedTeamMembers = action.payload.data;
      state.total = action.payload.total;
      state.loadingSelected = "succeeded";
    });
  },
});

export const { filterLabel, filterAllLabels, filterBytext, filterMembersBytext } = userTeamsSlice.actions;

export default userTeamsSlice.reducer;
